import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { UseAdminQuoteDetailReturn } from '../../hooks/useAdminQuoteDetail';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { ArrowLeft, User, Mail, Phone, Building } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { QuoteInfoSection } from './QuoteInfoSection';
import { QuoteResponsesSection } from './QuoteResponsesSection';
import { QuoteAttachmentsSection } from './QuoteAttachmentsSection';

interface AdminQuoteDetailProps extends UseAdminQuoteDetailReturn {}

export const AdminQuoteDetail: React.FC<AdminQuoteDetailProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    quote,
    contactedCompanies,
    loading,
    isResponseModalOpen,
    setIsResponseModalOpen,
    transportationCompanies,
    responseForm,
    setResponseForm,
    fieldErrors,
    setFieldErrors,
    submittingResponse,
    editingResponseId,
    setEditingResponseId,
    formErrors,
    setFormErrors,
    markingContactedId,
    handleEditResponse,
    handleDeleteResponse,
    handleMarkContacted,
    handleRejectQuote,
    handleSubmitResponse,
    handleDownloadAllImages,
    handleDownloadImage,
    formatBytes,
    getStatusBadgeVariant,
    getStatusBadgeClass,
  } = props;

  // ---- UI wrappers that add confirmation dialogs / alerts ----------------

  const onRejectQuote = async () => {
    if (!window.confirm(t('admin.quote.confirm_reject'))) {
      return;
    }
    try {
      await handleRejectQuote();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.quote.reject_failed'));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!quote) {
    return <div className="text-center py-10">{t('admin.quotes.not_found')}</div>;
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/quotes')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl tracking-tight flex items-center gap-3">
              {quote.tracking_number}
              <Badge variant={getStatusBadgeVariant(quote.status)} className={`text-base ${getStatusBadgeClass(quote.status)}`}>
                {(t(`admin.quotes.status.${quote.status}`, { defaultValue: quote.status }) as string).toUpperCase()}
              </Badge>
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('admin.quotes.created', {
                time: formatDistanceToNow(new Date(quote.created_at), { addSuffix: true })
              })}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          {quote.status !== 'rejected' && (
            <Button
              variant="destructive"
              size="sm"
              onClick={onRejectQuote}
              disabled={loading}
            >
              {t('admin.quote.reject_quote')}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Main Info Column */}
        <div className="md:col-span-2 space-y-6">
          <QuoteInfoSection quote={quote} />
          <QuoteResponsesSection
            quote={quote}
            transportationCompanies={transportationCompanies}
            responseForm={responseForm}
            setResponseForm={setResponseForm}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
            submittingResponse={submittingResponse}
            editingResponseId={editingResponseId}
            setEditingResponseId={setEditingResponseId}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            markingContactedId={markingContactedId}
            isResponseModalOpen={isResponseModalOpen}
            setIsResponseModalOpen={setIsResponseModalOpen}
            handleEditResponse={handleEditResponse}
            handleDeleteResponse={handleDeleteResponse}
            handleMarkContacted={handleMarkContacted}
            handleSubmitResponse={handleSubmitResponse}
          />
        </div>

        {/* Sidebar Column */}
        <div className="space-y-6">
          {/* Client Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="h-4 w-4" /> {t('admin.quote.client_details')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <span className="block text-xs text-slate-500 uppercase">{t('admin.quote.contact_name')}</span>
                <span className="font-medium">{quote.contact_name || t('common.not_available')}</span>
              </div>
              <div>
                <span className="block text-xs text-slate-500 uppercase">{t('admin.quote.contact_email')}</span>
                <span className="font-medium flex items-center gap-1">
                  <Mail className="h-3 w-3 text-slate-500" /> {quote.contact_email}
                </span>
              </div>
              {quote.contact_phone && (
                <div>
                  <span className="block text-xs text-slate-500 uppercase">{t('admin.quote.contact_phone')}</span>
                  <a href={`tel:${quote.contact_phone}`} className="font-medium text-blue-600 flex items-center gap-1 hover:underline">
                    <Phone className="h-3 w-3" /> {quote.contact_phone}
                  </a>
                </div>
              )}
              {quote.contact_company && (
                <div>
                  <span className="block text-xs text-slate-500 uppercase">{t('admin.quote.contact_company')}</span>
                  <span className="font-medium flex items-center gap-1">
                    <Building className="h-3 w-3" /> {quote.contact_company}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contacted Companies */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="h-4 w-4" /> {t('admin.quote.contacted_companies')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {contactedCompanies.length === 0 ? (
                <p className="text-sm text-slate-500">{t('admin.quote.no_companies_contacted')}</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                  {contactedCompanies.map((contact) => (
                    <div key={contact.id} className="text-sm border-b pb-2 last:border-0">
                      <div className="flex justify-between items-start">
                        <span className="font-medium">
                          {t('admin.quote.company_name_value', { name: contact.company_name })}
                        </span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0 h-5">
                          {t(`admin.quotes.status.${contact.status}`, { defaultValue: contact.status })}
                        </Badge>
                      </div>
                      <span className="text-xs text-slate-500 block mt-1">
                        {t('admin.quote.contacted_at', {
                          time: formatDistanceToNow(new Date(contact.contacted_at), { addSuffix: true })
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cargo Images / Attachments */}
          {quote.cargo_images && quote.cargo_images.length > 0 && (
            <QuoteAttachmentsSection
              cargo_images={quote.cargo_images}
              handleDownloadAllImages={handleDownloadAllImages}
              handleDownloadImage={handleDownloadImage}
              formatBytes={formatBytes}
            />
          )}
        </div>
      </div>
    </div>
  );
};
